/**
 * Runtime Configuration Service
 *
 * Fetches complete, validated store configuration from ConfigService.
 * The config is built from:
 * - Static data from store-label files (config.json, foundation.json)
 * - Dynamic data from Medusa seed process (publishable key, region, etc.)
 *
 * NO FALLBACK LOGIC - if ConfigService returns a config, it's complete.
 * Missing fields result in errors, not silent fallbacks.
 */

/**
 * Store configuration returned by ConfigService
 * All fields are required - no optional fallbacks
 */
export interface StoreConfig {
	storeLabel: string;
	// api-gateway tenant routing segment (/api/v1/tenants/<tenantPrefix>/...).
	// Folder name under terraform/store-labels; may differ from storeLabel
	// when the store-label's config.json sets label_public_key to something
	// other than its folder name (e.g. folder "tayrona" with
	// label_public_key "tayrona-store-v1"). Optional because deployments
	// from before this field was added in ConfigService still need to work;
	// fallback to storeLabel below.
	tenantPrefix?: string;
	displayName: string;
	enabled: boolean;
	medusa: {
		publishableKey: string;
		backendUrl: string;
		regionId: string;
		salesChannelId: string;
		currencyCode: string;
	};
	payment: {
		stripePublishableKey: string;
		stripeAccountId: string;
		platformFee?: {
			percentage: number;
			fixedAmount: number;
		};
	};
	storefront: {
		url: string;
		tld: string;
	};
	// Per-store i18n catalog (issue #140). Present when ConfigService /config
	// (and the baked runtime-config.json) carry the locales block, letting the
	// LocaleSwitcher render synchronously on first paint instead of gating on a
	// separate async fetch.
	locales?: LocalesCatalog;
}

export interface LocaleCatalogEntry {
	locale: string;
	currency: string;
}

export interface LocalesCatalog {
	default: string;
	supported: LocaleCatalogEntry[];
	switcherStyle: "pill" | "minimal" | "iconOnly";
}

/**
 * Error thrown when configuration cannot be loaded
 */
export class ConfigLoadError extends Error {
	constructor(
		public readonly source: string,
		public readonly reason: string,
	) {
		super(`Failed to load config from ${source}: ${reason}`);
		this.name = "ConfigLoadError";
	}
}

// Cached config promise
let configPromise: Promise<StoreConfig> | null = null;
// Synchronous snapshot of the resolved config, set once the promise settles.
// The App gates all rendering on config load, so by the time any component
// (e.g. LocaleSwitcher) mounts this is populated — letting state initialisers
// read config-derived data (like the locales catalog) without an extra async
// round-trip. Null before the first successful load. (issue #140)
let cachedConfig: StoreConfig | null = null;

/**
 * Synchronous accessor for the last resolved config. Returns null until the
 * first successful fetchConfig() completes. Use only where an async fetch is
 * already guaranteed to have run (i.e. below the App's config-loaded gate).
 */
export function getCachedConfig(): StoreConfig | null {
	return cachedConfig;
}

/**
 * Get the store label from environment or window
 */
function getStoreLabel(): string {
	return (
		window.__ENV__?.REACT_APP_STORE_LABEL ||
		import.meta.env.VITE_STORE_LABEL ||
		"intersum-ecommerce-example"
	);
}

// Cached runtime config from JSON file
let runtimeConfigJson: Record<string, unknown> | null = null;

/**
 * Load runtime-config.json for API URL discovery
 * This is used when falling back to ConfigService
 */
async function loadRuntimeConfigJson(): Promise<Record<
	string,
	unknown
> | null> {
	if (runtimeConfigJson !== null) {
		return runtimeConfigJson;
	}

	try {
		const response = await fetch("/runtime-config.json");
		if (response.ok) {
			runtimeConfigJson = await response.json();
			return runtimeConfigJson;
		}
	} catch {
		// Ignore errors - will use env vars instead
	}

	runtimeConfigJson = {};
	return null;
}

/**
 * Build ConfigService URL
 * Checks multiple sources in priority order
 */
async function getConfigServiceUrl(): Promise<string> {
	// 1. Check for explicit ConfigService URL env var
	const configServiceUrl = import.meta.env.VITE_CONFIG_SERVICE_URL;
	if (configServiceUrl) {
		return configServiceUrl;
	}

	// 2. Check environment variables for API URL
	const envApiUrl =
		import.meta.env.VITE_API_URL || window.__ENV__?.REACT_APP_API_URL;
	if (envApiUrl) {
		return envApiUrl;
	}

	// 3. Load from runtime-config.json (Cloudflare Pages deployments)
	const runtimeConfig = await loadRuntimeConfigJson();
	if (runtimeConfig) {
		const deployment = runtimeConfig.deployment as
			| Record<string, string>
			| undefined;
		const apiUrl =
			deployment?.api_url ||
			(runtimeConfig.configServiceUrl as string | undefined)?.replace(
				/\/config\/.*$/,
				"",
			);
		if (apiUrl) {
			return apiUrl;
		}
	}

	return "";
}

/**
 * Fetch configuration from ConfigService
 */
async function fetchFromConfigService(
	storeLabel: string,
): Promise<StoreConfig> {
	const baseUrl = await getConfigServiceUrl();
	if (!baseUrl) {
		throw new ConfigLoadError("config-service", "No API URL configured");
	}

	const url = `${baseUrl}/config/${storeLabel}`;
	console.log("[Config] Fetching from ConfigService:", url);

	const response = await fetch(url);

	if (!response.ok) {
		const errorBody = await response.json().catch(() => ({}));
		throw new ConfigLoadError(
			"config-service",
			`HTTP ${response.status}: ${errorBody.message || response.statusText}`,
		);
	}

	const config = (await response.json()) as StoreConfig;
	console.log("[Config] Loaded from ConfigService");
	// ConfigService caches the bare api-gateway URL; the tenant prefix is
	// composed at load-time so a single cache entry serves all consumers.
	// withTenantPrefix is idempotent — safe if upstream ever pre-applies.
	const prefix = config.tenantPrefix ?? config.storeLabel ?? storeLabel;
	return {
		...config,
		medusa: {
			...config.medusa,
			backendUrl: withTenantPrefix(config.medusa?.backendUrl ?? "", prefix),
		},
	};
}

/**
 * Fetch configuration from local runtime-config.json (Cloudflare Pages)
 */
async function fetchFromLocalFile(): Promise<StoreConfig | null> {
	try {
		const rawConfig = await loadRuntimeConfigJson();
		if (!rawConfig || Object.keys(rawConfig).length === 0) {
			return null;
		}

		console.log("[Config] Loaded from /runtime-config.json");

		// Check if runtime-config.json already contains full StoreConfig format
		// (pre-cached by CI/CD from ConfigService)
		const medusa = rawConfig.medusa as Record<string, string> | undefined;
		if (medusa?.publishableKey) {
			// Even in full-StoreConfig form the tenant prefix is composed at
			// load time so the upstream cache can keep storing the bare
			// api-gateway URL — keeps cache invariant simple. withTenantPrefix
			// is idempotent, so re-applying is a no-op.
			const cfg = rawConfig as unknown as StoreConfig;
			const prefix = cfg.tenantPrefix ?? cfg.storeLabel;
			return {
				...cfg,
				medusa: {
					...cfg.medusa,
					backendUrl: withTenantPrefix(cfg.medusa.backendUrl, prefix),
				},
			};
		}

		// Otherwise map from the legacy runtime-config.json format
		return mapRuntimeConfigToStoreConfig(rawConfig);
	} catch {
		return null;
	}
}

/**
 * Tenant-aware api-gateway path prefix (issue #168). Storefronts call the
 * gateway under `/api/v1/tenants/<store_label>/...` so the gateway can route
 * each request to its tenant's Medusa Cloud Run. The runtime-config files
 * carry only the BASE api-gateway URL; the tenant prefix is composed here
 * from the store label so the data files don't need to know about routing.
 */
function withTenantPrefix(baseUrl: string, storeLabel: string): string {
	if (!baseUrl || !storeLabel) return baseUrl;
	const trimmed = baseUrl.replace(/\/+$/, "");
	const expected = `/api/v1/tenants/${storeLabel}`;
	return trimmed.endsWith(expected) ? trimmed : `${trimmed}${expected}`;
}

/**
 * Map runtime-config.json format to StoreConfig
 * Used for Cloudflare Pages deployments
 */
function mapRuntimeConfigToStoreConfig(
	raw: Record<string, unknown>,
): StoreConfig {
	const deployment = raw.deployment as Record<string, string> | undefined;
	const auth = raw.auth as Record<string, string> | undefined;
	const store = raw.store as Record<string, string> | undefined;
	const region = raw.region as Record<string, string> | undefined;
	const keys = raw.keys as Record<string, string> | undefined;

	const storeLabel = (store?.label || raw.store_label || "") as string;
	// Legacy runtime-config.json shape predates the tenantPrefix field, so
	// fall back to storeLabel — matches the pre-existing behaviour for the
	// two tenants where dir name == label_public_key.
	const tenantPrefix = (raw.tenantPrefix || storeLabel) as string;
	const baseApiUrl = (deployment?.api_url ||
		deployment?.backend_url ||
		raw.backend_url ||
		"") as string;

	return {
		storeLabel,
		tenantPrefix,
		displayName: (store?.name || "Store") as string,
		enabled: true,
		medusa: {
			publishableKey: (auth?.publishable_key ||
				raw.publishable_key ||
				"") as string,
			backendUrl: withTenantPrefix(baseApiUrl, tenantPrefix),
			regionId: (region?.id || raw.region_id || "") as string,
			salesChannelId: (raw.sales_channel_id || "") as string,
			currencyCode: (region?.currency_code ||
				raw.currency_code ||
				"") as string,
		},
		payment: {
			stripePublishableKey: (keys?.stripePublishableKey ||
				raw.stripe_publishable_key ||
				"") as string,
			stripeAccountId: (keys?.stripeAccountId || "") as string,
			platformFee: (keys?.platformFee ||
				undefined) as StoreConfig["payment"]["platformFee"],
		},
		storefront: {
			url: (raw.storefront_url ||
				`https://${raw.tld || "localhost"}`) as string,
			tld: (raw.tld || "localhost") as string,
		},
		locales: raw.locales as LocalesCatalog | undefined,
	};
}

/**
 * Backfill the per-store locales catalog from the dedicated ConfigService
 * endpoint (`/config/<label>/locales`) when the resolved config didn't embed it.
 * Returns undefined on any failure so the caller can degrade gracefully.
 */
async function fetchLocalesCatalogOrUndefined(
	config: StoreConfig,
): Promise<LocalesCatalog | undefined> {
	try {
		const base = (await getConfigServiceUrl()).replace(/\/$/, "");
		if (!base) return undefined;
		const label = config.tenantPrefix || config.storeLabel || getStoreLabel();
		const response = await fetch(`${base}/config/${label}/locales`, {
			credentials: "omit",
		});
		if (!response.ok) return undefined;
		return (await response.json()) as LocalesCatalog;
	} catch {
		return undefined;
	}
}

/**
 * Fetch and cache store configuration
 *
 * Priority:
 * 1. Local /runtime-config.json (Cloudflare Pages builds)
 * 2. ConfigService API (Cloud Run)
 */
export async function fetchConfig(): Promise<StoreConfig> {
	if (!configPromise) {
		configPromise = (async () => {
			// Try local file first (Cloudflare Pages)
			const localConfig = await fetchFromLocalFile();
			let resolved: StoreConfig;
			if (localConfig?.medusa.publishableKey) {
				resolved = localConfig;
			} else {
				// Prefer store label from runtime-config.json (already loaded above),
				// then fall back to env vars / default, and load from ConfigService.
				const storeLabel = localConfig?.storeLabel || getStoreLabel();
				resolved = await fetchFromConfigService(storeLabel);
			}

			// The /:locale routing (issue #221) needs the locales catalog
			// synchronously at first render (LocaleGate redirects the root path to
			// the store-default locale). The dev/CI local runtime-config.json uses a
			// legacy shape that doesn't embed `locales`, so backfill it from the
			// dedicated ConfigService endpoint before the config resolves. (The
			// production pre-cached runtime-config.json already embeds it — then this
			// is a no-op.)
			if (!resolved.locales) {
				const locales = await fetchLocalesCatalogOrUndefined(resolved);
				if (locales) {
					resolved = { ...resolved, locales };
				}
			}
			return resolved;
		})();
		// Populate the synchronous snapshot once resolved.
		configPromise.then((resolved) => {
			cachedConfig = resolved;
		}).catch(() => {
			/* keep cachedConfig null on failure */
		});
	}

	return configPromise;
}

/**
 * Clear cached configuration (for testing)
 */
export function clearConfigCache(): void {
	configPromise = null;
	cachedConfig = null;
}

/**
 * Legacy alias for backward compatibility
 */
export const fetchRuntimeConfig = fetchConfig;

// Window.__ENV__ type is declared in vite-env.d.ts
